(() => {
  if (window.tpStickyProductGallery) return;

  const desktop = window.matchMedia('(min-width: 1001px)');
  const instances = new Map();
  const selector = '[data-tp-sticky-gallery]';
  const properties = ['height', 'top', 'left', 'width'];
  const gap = 12;

  function findLimit(slot) {
    const main = slot.closest('#MainContent');
    if (!main) return null;
    return Array.from(main.querySelectorAll('.tp-comparison-heading.ps-section__heading'))
      .find(heading => heading.getClientRects().length &&
        Boolean(slot.compareDocumentPosition(heading) & Node.DOCUMENT_POSITION_FOLLOWING));
  }

  function headerOffset(header) {
    if (!header) return 0;
    const mode = header.getAttribute('sticky');
    if (mode !== 'always' && header.dataset.stickyState !== 'active') return 0;
    const rect = header.getBoundingClientRect();
    if (rect.bottom <= 0 || Number(getComputedStyle(header).opacity) === 0) return 0;
    return Math.max(0, Math.min(window.innerHeight, rect.bottom));
  }

  function mount(slot) {
    if (instances.has(slot) || slot.closest('dialog, .quick-add-modal, .qa-only')) return;
    const panel = slot.querySelector(':scope > .tp-product-gallery-rail');
    const limit = findLimit(slot);
    if (!panel || !panel.querySelector('media-gallery') || !limit) return;

    const events = new AbortController();
    const header = document.getElementById('header-component');
    const main = slot.closest('#MainContent');
    const values = new Map();
    let frame = 0;
    let destroyed = false;

    function reset() {
      slot.classList.remove('tp-gallery-is-sticky');
      properties.forEach(name => slot.style.removeProperty(`--tp-gallery-${name}`));
      values.clear();
    }

    function set(name, value) {
      const rounded = `${Math.round(value * 100) / 100}px`;
      if (values.get(name) === rounded) return;
      values.set(name, rounded);
      slot.style.setProperty(`--tp-gallery-${name}`, rounded);
    }

    function update() {
      frame = 0;
      if (destroyed) return;
      if (!desktop.matches || !slot.isConnected || !limit.isConnected) {
        reset();
        return;
      }

      // All reads precede writes. No video/image cloning or DOM relocation.
      const anchor = slot.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const boundary = limit.getBoundingClientRect();
      if (!anchor.width || !panelRect.height || !limit.getClientRects().length) {
        reset();
        return;
      }

      const preferredTop = headerOffset(header) + gap;
      // A tall panel scrolls naturally until its bottom becomes reachable.
      const top = Math.min(preferredTop, window.innerHeight - panelRect.height - gap);
      const availableTravel = boundary.top - gap - panelRect.height - anchor.top;
      if (availableTravel <= 0 || anchor.top >= top) {
        reset();
        return;
      }

      set('height', panelRect.height);
      set('left', anchor.left);
      set('width', anchor.width);
      // At the limit the panel moves upwards with the comparison heading.
      set('top', Math.min(top, boundary.top - panelRect.height - gap));
      slot.classList.add('tp-gallery-is-sticky');
    }

    function schedule() {
      if (!destroyed && !frame) frame = requestAnimationFrame(update);
    }

    const options = { passive: true, signal: events.signal };
    window.addEventListener('scroll', schedule, options);
    window.addEventListener('resize', schedule, options);
    window.addEventListener('pageshow', schedule, options);
    main.addEventListener('load', schedule, { capture: true, signal: events.signal });
    main.addEventListener('toggle', schedule, { capture: true, signal: events.signal });

    let sizeObserver;
    if ('ResizeObserver' in window) {
      sizeObserver = new ResizeObserver(schedule);
      [slot, panel, main, header, limit].filter(Boolean).forEach(node => sizeObserver.observe(node));
    }
    let headerObserver;
    if (header && 'MutationObserver' in window) {
      headerObserver = new MutationObserver(schedule);
      headerObserver.observe(header, {
        attributes: true,
        attributeFilter: ['data-sticky-state', 'data-animating', 'sticky', 'style']
      });
    }
    if (document.fonts) document.fonts.ready.then(schedule);

    instances.set(slot, () => {
      destroyed = true;
      cancelAnimationFrame(frame);
      events.abort();
      sizeObserver?.disconnect();
      headerObserver?.disconnect();
      reset();
      instances.delete(slot);
    });
    schedule();
  }

  function refresh() {
    instances.forEach((cleanup, slot) => {
      if (!desktop.matches || !slot.isConnected) cleanup();
    });
    if (desktop.matches) document.querySelectorAll(`#MainContent ${selector}`).forEach(mount);
  }

  desktop.addEventListener('change', refresh);
  document.addEventListener('shopify:section:load', () => {
    instances.forEach(cleanup => cleanup());
    refresh();
  });
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((cleanup, slot) => {
      if (event.target.contains(slot) || event.target.querySelector('.tp-comparison-heading.ps-section__heading')) cleanup();
    });
  });
  window.tpStickyProductGallery = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
